import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail, ShieldAlert } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from './OnboardingContext';
import DemoPreview from './components/DemoPreview';
import InboxMockup from './components/InboxMockup';
import ProviderConnectModal from './components/ProviderConnectModal';
import { Button } from '@/components/ui/button';
import { getGoogleIntegrationStatus, syncGoogleData, startGoogleOAuth } from '../../lib/api';
import { finalizeRealConnectionAndEnter } from './finalizeRealConnection';

/**
 * Step 1 — Inbox. Preview → Connect, multi-provider edition.
 *
 * ROOT CAUSE FIX (Google OAuth "loops back to the connect modal"):
 * this page used to be 100% blind to the backend — it only knew about a
 * connection through the one-shot `?google_connected=success` query
 * param handled in <OnboardingShell />. If that param was ever lost
 * (redirect hiccup, refresh, browser back/forward, user typing the URL
 * again) there was NO fallback: the UI defaulted straight back to
 * "Elige tu proveedor de correo" even for an already-connected account.
 *
 * The backend (GET /api/integrations/google/status) is now the single
 * source of truth. On every mount we ask it first, BEFORE rendering any
 * connect UI:
 *   - connected && status==='ok' && !reauthorization_required
 *       → mark inbox/calendar as real, best-effort sync, then leave the
 *         wizard entirely via finalizeRealConnectionAndEnter (never
 *         shows the picker again).
 *   - reauthorization_required || status==='permission_missing'
 *       → dedicated "necesita permisos" screen with a Reauthorize CTA.
 *         This is the ONLY case where we intentionally start OAuth
 *         again — never automatically, always via an explicit click.
 *   - anything else (never connected) → today's connect flow, unchanged.
 *
 * Skip path: marks the step as demo, advances to /welcome/calendar.
 */
export default function StepInbox() {
  const { t } = useLanguage();
  const { markStepConnected, markStepSkipped, reset } = useOnboarding();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  // 'checking' | 'ready' | 'permission_missing' | 'finalizing'
  const [reconcileState, setReconcileState] = useState('checking');
  const [missingScopesLabel, setMissingScopesLabel] = useState('');
  const reauthorizingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let status = null;
      try {
        status = await getGoogleIntegrationStatus();
      } catch {
        // Status check failed outright (network/backend hiccup) — never
        // trap the user on a blank screen, just fall through to the
        // normal connect flow so they can still try.
        if (!cancelled) setReconcileState('ready');
        return;
      }
      if (cancelled) return;

      const isConnected = !!status?.connected && status?.status === 'ok' && !status?.reauthorization_required;
      if (isConnected) {
        setReconcileState('finalizing');
        markStepConnected('inbox', 'real');
        markStepConnected('calendar', 'real');
        // Best-effort sync. A failure here must NEVER re-show the
        // connect flow — the real Smart Inbox already surfaces a
        // "Reintentar sincronización" affordance via DataModeBanner
        // once we land there, so we just proceed regardless.
        try {
          await syncGoogleData();
        } catch {
          /* handled on /inbox via DataModeBanner's retry button */
        }
        if (cancelled) return;
        await finalizeRealConnectionAndEnter({ refresh, navigate, resetOnboarding: reset });
        return;
      }

      const needsReauth = status?.configured && (status?.reauthorization_required || status?.status === 'permission_missing');
      if (needsReauth) {
        const scopes = (status?.missing_scopes || [])
          .map((s) => s.split('/').pop().replace(/\./g, ' '))
          .join(', ');
        setMissingScopesLabel(scopes || t('welcome.connect_modal.microsoft_subtitle'));
        setReconcileState('permission_missing');
        return;
      }

      setReconcileState('ready');
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReauthorize = async () => {
    if (reauthorizingRef.current) return;
    reauthorizingRef.current = true;
    try {
      const { auth_url } = await startGoogleOAuth('/welcome/inbox');
      if (auth_url) window.location.href = auth_url;
    } finally {
      reauthorizingRef.current = false;
    }
  };

  // Note: DemoPreview's onConnect awaits the returned promise. Resolving
  // *before* the redirect happens would put DemoPreview back into
  // 'decision' stage; instead we keep the promise pending so the spinner
  // remains until the browser actually navigates. If the user closes
  // the modal we resolve manually so DemoPreview returns to its
  // decision state.
  const handleConnect = () => {
    return new Promise((resolve) => {
      setPickerOpen(true);
      // Stash resolver on the window so we can call it from the
      // onOpenChange handler without prop-drilling.
      window.__quantroPickerResolve = resolve;
    });
  };

  const handleOpenChange = (open) => {
    setPickerOpen(open);
    if (!open) {
      window.__quantroPickerResolve?.();
      window.__quantroPickerResolve = null;
    }
  };

  const handleSkip = () => {
    markStepSkipped('inbox');
    markStepConnected('inbox', 'demo');
    navigate('/welcome/calendar');
  };

  if (reconcileState === 'checking' || reconcileState === 'finalizing') {
    return (
      <div className="flex flex-col items-center justify-center text-center min-h-[50vh]" data-testid="step-inbox-reconciling">
        <Loader2 size={28} className="animate-spin text-[hsl(var(--primary))] mb-4" />
        <p className="text-sm text-muted-foreground">{t('welcome.inbox.checking_connection')}</p>
      </div>
    );
  }

  if (reconcileState === 'permission_missing') {
    return (
      <div className="flex flex-col items-center text-center max-w-lg mx-auto" data-testid="step-inbox-permission-missing">
        <div className="size-16 rounded-2xl bg-[hsl(var(--warning)/0.10)] text-[hsl(var(--warning))] flex items-center justify-center mb-6">
          <ShieldAlert size={28} />
        </div>
        <h1 className="font-display text-2xl md:text-4xl font-semibold tracking-tight mb-3 text-balance">
          {t('welcome.connect_modal.permission_missing_title')}
        </h1>
        <p className="text-base text-muted-foreground mb-8 leading-relaxed text-balance">
          {t('welcome.connect_modal.permission_missing_desc', { provider: 'Google', scopes: missingScopesLabel })}
        </p>
        <Button onClick={handleReauthorize} data-testid="step-inbox-reauthorize-button">
          {t('welcome.connect_modal.reauthorize_cta')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center" data-testid="step-inbox-page">
      <div className="size-16 rounded-2xl bg-[hsl(var(--primary)/0.10)] text-[hsl(var(--primary))] flex items-center justify-center mb-6">
        <Mail size={28} />
      </div>
      <h1 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mb-3 text-balance" data-testid="step-inbox-title">
        {t('welcome.inbox.title')}
      </h1>
      <p className="text-base md:text-lg text-muted-foreground max-w-xl mb-10 leading-relaxed text-balance whitespace-pre-line">
        {t('welcome.inbox.subtitle')}
      </p>

      <DemoPreview
        testIdPrefix="step-inbox"
        microcopyPreview={t('welcome.inbox.preview_microcopy')}
        microcopyEnd={t('welcome.inbox.preview_end_microcopy')}
        connectLabel={t('welcome.inbox.connect_cta')}
        skipLabel={t('welcome.inbox.skip_cta')}
        phases={[
          { key: 'analyzing', delay_ms: 0,    label: t('welcome.inbox.phase_analyzing') },
          { key: 'detecting', delay_ms: 1800, label: t('welcome.inbox.phase_detecting') },
          { key: 'priorit',   delay_ms: 3600, label: t('welcome.inbox.phase_priorit')   },
        ]}
        previewDurationMs={7000}
        onConnect={handleConnect}
        onSkip={handleSkip}
      >
        <InboxMockup />
      </DemoPreview>

      <ProviderConnectModal
        open={pickerOpen}
        onOpenChange={handleOpenChange}
        step="inbox"
        returnTo="/welcome/inbox"
        onAlreadyConnected={() => {
          markStepConnected('inbox', 'real');
          markStepConnected('calendar', 'real');
          finalizeRealConnectionAndEnter({ refresh, navigate, resetOnboarding: reset });
        }}
      />
    </div>
  );
}
